import { resources, common, enums } from '../../../gads'
import { BiddingStrategy } from './../../../bidding-strategies/bidding_strategies'
import { GadsFieldsRequired } from '../../../utils/global'

export namespace ImpTypes {
    export const Error = {
        NO_ENABLED_DISPLAY_CAMPAIGNS: 'no_enabled_display_campaigns',
        NO_RECOMMENDED_EXCLUSIONS_FOUND: 'no_recommend_exclusions_found',
    } as const

    export type Error = (typeof Error)[keyof typeof Error]

    export const ErrorReadable = {
        [Error.NO_ENABLED_DISPLAY_CAMPAIGNS]: 'No enabled display campaigns found in the account',
        [Error.NO_RECOMMENDED_EXCLUSIONS_FOUND]: 'No placements to exclude found in the account',
    }

    export type Body = RecommendedExclusion
    // eslint-disable-next-line @typescript-eslint/no-empty-interface
    export interface ExtraDetails {}
}

// --
// Data processing types, not explicitly related to improvement types
// --

export type CampaignFields = GadsFieldsRequired<
    resources.Campaign,
    'resource_name' | 'name' | 'advertising_channel_type' | 'advertising_channel_sub_type'
>

export interface DisplayCampaign {
    campaign: CampaignFields
}

type PlacementFields = Required<resources.GroupPlacementView>

export type TopMobileAppFields = GadsFieldsRequired<
    resources.GroupPlacementView,
    'placement' | 'display_name'
>

export interface MobileApp {
    name: string
    app_id: string
}

export type PlacementMetrics = GadsFieldsRequired<
    common.Metrics,
    | 'impressions'
    | 'clicks'
    | 'cost_micros'
    | 'all_conversions'
    | 'view_through_conversions'
    | 'all_conversions_value'
>

export enum ContentPerformanceMode {
    Cpa = BiddingStrategy.Cpa,
    Roas = BiddingStrategy.Roas,
}

// Extended placement performance metrics calculated in the improvement
export interface PlacementPerformanceMetrics extends PlacementMetrics {
    combined_conv: number
    combined_conv_value: number
    combined_conv_rate: number
    min_clicks: number
    cpa: number
    roas: number
    cost: number
}

export interface RecommendedExclusion {
    placement: {
        resource_names: string[]
        display_name: string
        placement: string
        placement_type: enums.PlacementType
        target_url: string
        youtube_channel_metadata?: YoutubeChannelMetadata
    }
    placement_metrics: PlacementPerformanceMetrics
    environment_metrics: PlacementPerformanceMetrics
    performance_mode: ContentPerformanceMode
    exclusion_level: ExclusionLevel
    exclusion_type: ExclusionType
    scatter_plot_points?: ScatterPlotPoint[]
    campaigns_queried: CampaignUsedForStats[]
    top_mobile_apps?: MobileApp[]
    lookbackWindow: number
}

export interface YoutubeChannelMetadata {
    title: string
    description: string
    subscriber_count: number
    thumbnail_url: string
    topics?: string[]
}

export interface ScatterPlotPoint {
    cost: number
    all_conversions: number
    all_conversions_value: number
    label: string
    is_current_placement?: boolean
}

export enum ExclusionLevel {
    ACCOUNT = 1,
    CAMPAIGN = 2,
}

export interface NewExclusions {
    [ExclusionLevel.ACCOUNT]: RecommendedExclusion[]
    [ExclusionLevel.CAMPAIGN]: RecommendedExclusion[]
}

export interface GroupPlacement {
    campaign: CampaignFields
    group_placement_view: PlacementFields
    metrics: PlacementMetrics
}

interface PlacementsByType {
    // type is enums.PlacementType
    [type: number]: GroupPlacement[]
}

export interface AllPlacements {
    placements: PlacementsByType
    totals: PlacementMetrics
}

type DeepRequired<T> = Required<{ [P in keyof T]: DeepRequired<T[P]> }>

export interface CustomerNegativeCriterion {
    customer_negative_criterion: DeepRequired<
        Pick<
            resources.CustomerNegativeCriterion,
            | 'resource_name'
            | 'type'
            | 'placement'
            | 'mobile_application'
            | 'mobile_app_category'
            | 'youtube_channel'
            | 'youtube_video'
        >
    >
}

export interface CampaignNegativeCriterion {
    campaign_criterion: DeepRequired<
        Pick<
            resources.CampaignCriterion,
            | 'resource_name'
            | 'type'
            | 'placement'
            | 'mobile_application'
            | 'mobile_app_category'
            | 'youtube_channel'
            | 'youtube_video'
            | 'campaign'
        >
    >
}

export interface AdGroupNegativeCriterion {
    ad_group_criterion: DeepRequired<
        Pick<
            resources.AdGroupCriterion,
            | 'resource_name'
            | 'type'
            | 'placement'
            | 'mobile_application'
            | 'mobile_app_category'
            | 'youtube_channel'
            | 'youtube_video'
        >
    >
    ad_group: {
        campaign: string
    }
}

export interface SharedSetCriterion {
    shared_criterion: DeepRequired<
        Pick<
            resources.SharedCriterion,
            | 'resource_name'
            | 'type'
            | 'placement'
            | 'mobile_application'
            | 'mobile_app_category'
            | 'youtube_channel'
            | 'youtube_video'
            | 'shared_set'
        >
    >
    shared_set: DeepRequired<Pick<resources.SharedSet, 'resource_name'>>
}

export interface CriterionExclusions {
    [enums.PlacementType.WEBSITE]: string[]
    [enums.PlacementType.MOBILE_APPLICATION]: string[]
    [enums.PlacementType.YOUTUBE_CHANNEL]: string[]
    [enums.PlacementType.YOUTUBE_VIDEO]: string[]
}

export enum ExclusionType {
    MOBILE_APP = 'mobile_app',
    MOBILE_ALL = 'mobile_all',
    WEBSITE = 'website',
    YOUTUBE_ALL = 'youtube_all',
    YOUTUBE_CHANNEL = 'youtube_channel',
    GMAIL = 'gmail',
}

export interface CampaignUsedForStats {
    name: string
    resource_name: string
}

export interface NegativePlacementIndex {
    [id: string]: string[]
}
